import { View, type LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { SLIDE_SETTING_BLOCK_KINDS } from "@-/dev.improve.haniverse.lib.slide.pages";
import { useCallback, useContext, type ReactNode } from "react";

import { SlidePageBlockRenderContext } from "#slide-page-block-render-context";

/**
 * One top-level block of a slide. The Metro MDX transformer wraps every block in one
 * (`configs/metro/rehype-slide-page-blocks.cjs`); authors never write it. Its attributes arrive as
 * strings.
 *
 * Every visible block sits in its own `View`, in the measuring copy and on its page alike, so the
 * height measured is exactly the height shown. Setting blocks (`SlideFooter`, `SlideOptions`)
 * render nothing themselves; they mount once, with the first page.
 */
export function SlidePageBlock({
  block,
  children,
  count,
  keepWithNext,
  kind,
  section,
}: {
  block: string;
  children?: ReactNode;
  count: string;
  /** `"true"` when the block's text ends in a colon: it introduces the block after it. */
  keepWithNext?: string;
  kind: string;
  section: string;
}) {
  const render = useContext(SlidePageBlockRenderContext);
  const isSetting = SLIDE_SETTING_BLOCK_KINDS.has(kind);
  const onMeasure = render?.kind === "measure" ? render.onMeasure : null;
  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      onMeasure?.({
        block: Number(block),
        count: Number(count),
        height: event.nativeEvent.layout.height,
        keepWithNext: keepWithNext === "true",
        kind,
        section: Number(section),
      });
    },
    [block, count, keepWithNext, kind, onMeasure, section],
  );

  if (render === null) {
    return children;
  }
  if (render.kind === "measure") {
    return (
      <View collapsable={false} onLayout={handleLayout}>
        {isSetting ? null : children}
      </View>
    );
  }
  if (isSetting) {
    return render.isFirstPage ? children : null;
  }
  return render.blocks.has(Number(block)) ? <View>{children}</View> : null;
}
